import { useEffect, useRef, useState } from 'react'

interface Props {
  isOpen: boolean
  title: string
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  headerActions?: React.ReactNode
  /** Extra classes for the panel shell (e.g. a wider max width). */
  widthClassName?: string
}

/**
 * Non-blocking side panel (Task #649).
 *
 * Unlike `Modal`, this shell deliberately renders NO backdrop and NO
 * full-screen layer of any kind, so every click that lands outside the panel
 * reaches the page underneath: navigation, tabs, filters, + Add Topic and
 * other rows all stay usable while the panel is open. It also never locks
 * page scroll.
 *
 * Closing is entirely the parent's business — there is no outside-click
 * handler and Escape does NOT close it either, because the panel holds a live
 * edit form and an accidental keypress must never discard typed changes. The
 * only exits are the header X and whatever controls the parent puts in the
 * footer.
 *
 * It sits at z-45 — above fixed page chrome such as the meeting countdown bar
 * (z-40), which would otherwise paint over the panel's footer actions, and
 * below the z-50 modals (ConfirmModal, the Solved dialog, Convert to Action
 * Item), so those still render above the panel.
 *
 * So the page is genuinely usable and not merely un-dimmed, the panel also
 * reserves its own width by padding the document body while it is open: the
 * page reflows to the left of the panel instead of hiding right-aligned
 * controls (Create, + Add Topic) underneath it. On narrow screens the panel
 * takes the full width, so there is nothing to reflow and no padding applies.
 */
export default function SidePanel({
  isOpen,
  title,
  onClose,
  children,
  footer,
  headerActions,
  widthClassName = 'w-full sm:w-[30rem]',
}: Props) {
  // Drive the slide-in from a state flip on the first frame after mount so the
  // transition actually runs instead of the panel appearing already in place.
  const [entered, setEntered] = useState(false)
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!isOpen) {
      setEntered(false)
      return
    }
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [isOpen])

  // Reserve the panel's width so page content reflows beside it rather than
  // disappearing underneath it. Never locks scrolling.
  useEffect(() => {
    if (!isOpen) return

    const applyInset = () => {
      const width = panelRef.current?.offsetWidth ?? 0
      // Only reflow when a usable amount of page is left beside the panel;
      // on narrow screens the panel is (near) full width, so padding there
      // would just squash the page and force a horizontal scrollbar.
      const roomLeft = window.innerWidth - width
      document.body.style.paddingRight = width > 0 && roomLeft >= 320 ? `${width}px` : ''
    }

    applyInset()
    window.addEventListener('resize', applyInset)
    return () => {
      window.removeEventListener('resize', applyInset)
      document.body.style.paddingRight = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <aside
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      data-testid="side-panel"
      className={`fixed top-0 right-0 bottom-0 z-[45] flex flex-col bg-white shadow-elevated border-l border-[#351E1D]/10 transition-transform duration-200 ease-out ${widthClassName} ${
        entered ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex flex-wrap gap-2 items-center justify-between px-6 py-4 border-b border-[#351E1D]/10 flex-shrink-0">
        <h2 className="font-semibold text-text-primary" style={{ fontSize: '18px' }}>
          {title}
        </h2>
        <div className="flex items-center gap-2 ml-auto">
        {headerActions}
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title}`}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone/20 text-text-secondary hover:text-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

      {footer && (
        <div className="flex-shrink-0 border-t border-[#351E1D]/10 bg-white px-6 py-4">{footer}</div>
      )}
    </aside>
  )
}
